import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, Shield, CheckCircle2 } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
export function ChangePasswordScreen() {
  const {
    goBack
  } = useNavigation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Simple password strength calculation
  const getStrength = (pass: string) => {
    if (!pass) return 0;
    let score = 0;
    if (pass.length > 8) score += 1;
    if (/[A-Z]/.test(pass)) score += 1;
    if (/[0-9]/.test(pass)) score += 1;
    if (/[^A-Za-z0-9]/.test(pass)) score += 1;
    return score;
  };
  const strength = getStrength(newPassword);
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
  const strengthColors = ['bg-error', 'bg-warning', 'bg-yellow-400', 'bg-success', 'bg-primary'];
  const handleSave = () => {
    if (newPassword === confirmPassword && newPassword.length >= 8) {
      setShowSuccess(true);
      setTimeout(() => {
        goBack();
      }, 1500);
    }
  };
  const isFormValid = currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-297">
      <Header title="Change Password" data-id="element-298" />

      <div className="p-4 space-y-6" data-id="element-299">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-50 flex flex-col items-center text-center" data-id="element-300">
          <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4 text-primary" data-id="element-301">
            <Shield className="w-8 h-8" data-id="element-302" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-303">
            Create a strong password
          </h2>
          <p className="text-sm text-gray-500" data-id="element-304">
            Your new password must be at least 8 characters long and include a
            mix of letters, numbers, and symbols.
          </p>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50 space-y-4" data-id="element-305">
          <Input label="Current Password" type={showCurrent ? 'text' : 'password'} placeholder="••••••••" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} icon={<Lock className="w-4 h-4" data-id="element-307" />} rightIcon={<button onClick={() => setShowCurrent(!showCurrent)} className="p-1 hover:text-primary transition-colors" data-id="element-308">
                {showCurrent ? <EyeOff className="w-4 h-4" data-id="element-309" /> : <Eye className="w-4 h-4" data-id="element-310" />}
              </button>} data-id="element-306" />

          <div className="h-px bg-gray-100 my-2" data-id="element-311"></div>

          <div className="space-y-2" data-id="element-312">
            <Input label="New Password" type={showNew ? 'text' : 'password'} placeholder="••••••••" value={newPassword} onChange={e => setNewPassword(e.target.value)} icon={<Lock className="w-4 h-4" data-id="element-314" />} rightIcon={<button onClick={() => setShowNew(!showNew)} className="p-1 hover:text-primary transition-colors" data-id="element-315">
                  {showNew ? <EyeOff className="w-4 h-4" data-id="element-316" /> : <Eye className="w-4 h-4" data-id="element-317" />}
                </button>} data-id="element-313" />

            {/* Password Strength Indicator */}
            {newPassword.length > 0 && <div className="px-1" data-id="element-318">
                <div className="flex gap-1 mb-1.5 mt-2" data-id="element-319">
                  {[1, 2, 3, 4].map(level => <div key={level} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${strength >= level ? strengthColors[strength] : 'bg-gray-200'}`} data-id="element-320" />)}
                </div>
                <p className={`text-xs font-medium ${strength >= 3 ? 'text-success' : 'text-gray-500'}`} data-id="element-321">
                  {strengthLabels[strength]}
                </p>
              </div>}
          </div>

          <Input label="Confirm New Password" type={showConfirm ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} icon={<Lock className="w-4 h-4" data-id="element-323" />} rightIcon={<button onClick={() => setShowConfirm(!showConfirm)} className="p-1 hover:text-primary transition-colors" data-id="element-324">
                {showConfirm ? <EyeOff className="w-4 h-4" data-id="element-325" /> : <Eye className="w-4 h-4" data-id="element-326" />}
              </button>} error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined} data-id="element-322" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-327">
        <div className="max-w-md mx-auto" data-id="element-328">
          <Button fullWidth size="lg" onClick={handleSave} disabled={!isFormValid} className="shadow-lg shadow-primary/20" data-id="element-329">
            Update Password
          </Button>
        </div>
      </div>

      {/* Success Toast */}
      <AnimatePresence data-id="element-330">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-331">
            <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-332">
              <CheckCircle2 className="w-5 h-5 text-success" data-id="element-333" />
              <span className="text-sm font-medium" data-id="element-334">
                Password updated successfully
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}